import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IPersonFilter } from 'app/models/filters/person-filter';
import { IFirmApplicationPerson } from 'app/models/firms/firm-application-person';
import { IPerson } from 'app/models/people/person';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface IFirmApplicationPersonDialogData {
  person: IFirmApplicationPerson;
  applicationType: string;
  existingPeople: IFirmApplicationPerson[];
}

interface IEmailCandidate {
  email: string;
  typePriority: number;
  recency: number;
}

@Component({
  selector: 'app-firm-application-person-dialog',
  templateUrl: './firm-application-person-dialog.component.html',
  styleUrls: ['./firm-application-person-dialog.component.scss']
})
export class FirmApplicationPersonDialogComponent implements OnInit {
  public readonly disciplines = ['Risk Assessor', 'Supervisor', 'Worker', 'Project Designer', 'Inspector'];
  public personForm: FormGroup;
  public person: IFirmApplicationPerson;
  public matchingPeople: IPerson[] = [];
  public searchCompleted = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IFirmApplicationPersonDialogData,
    private dialogRef: MatDialogRef<FirmApplicationPersonDialogComponent>,
    private personService: PersonService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.person = { ...this.data.person, person: this.data.person.person ? { ...this.data.person.person } : null };
    this.personForm = new FormGroup({
      selectedPerson: new FormControl(this.person.person, [Validators.required, this.existingPersonValidator()]),
      discipline: new FormControl(this.person.discipline),
      firmIsPaying: new FormControl(this.person.firmIsPaying),
      atp: new FormControl(this.person.atp, this.isRrpApplication ? Validators.required : []),
      trainingDate: new FormControl(this.person.trainingDate ? new Date(this.person.trainingDate) : null, this.isRrpApplication ? Validators.required : []),
    });

    this.selectedPersonControl?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((value) => {
      if (typeof value !== 'string') {
        this.matchingPeople = [];
        this.searchCompleted = false;
        return;
      }

      this.searchExistingPeople(value);
    });
  }

  get isLbpaApplication(): boolean {
    return this.data.applicationType === 'lbpa';
  }

  get isRrpApplication(): boolean {
    return this.data.applicationType === 'rrp';
  }

  get selectedPersonControl(): AbstractControl | null {
    return this.personForm.get('selectedPerson');
  }

  get selectedPerson(): IPerson | null {
    const value = this.selectedPersonControl?.value;
    return value && typeof value !== 'string' ? value : null;
  }

  get selectedPersonErrorMessage(): string {
    const value = this.selectedPersonControl?.value;
    return typeof value === 'string' && value.trim().length < 3
      ? 'Type at least 3 characters, then choose a person from the list.'
      : 'Choose an existing person from the search results.';
  }

  displayPerson(value: IPerson | string | null): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    const fullName = this.getPersonFullName(value);
    const preferredEmail = this.getPreferredEmail(value);
    return preferredEmail ? fullName + ' - ' + preferredEmail : fullName;
  }

  showSelectedPersonRequiredError(): boolean {
    return !!this.selectedPersonControl && this.selectedPersonControl.hasError('required') && this.selectedPersonControl.touched;
  }

  showSelectedPersonLookupError(): boolean {
    return !!this.selectedPersonControl && this.selectedPersonControl.hasError('personSelectionRequired') && this.selectedPersonControl.touched;
  }

  save(): void {
    if (this.personForm.invalid) {
      this.personForm.markAllAsTouched();
      return;
    }

    const selectedPerson = this.selectedPerson;
    if (!selectedPerson) {
      this.selectedPersonControl?.markAsTouched();
      return;
    }

    const value = this.personForm.getRawValue();
    this.dialogRef.close({
      ...this.person,
      person: selectedPerson,
      firstName: selectedPerson.firstName,
      lastName: selectedPerson.lastName,
      discipline: value.discipline || null,
      firmIsPaying: value.firmIsPaying === true,
      atp: value.atp || null,
      trainingDate: value.trainingDate || null,
    } as IFirmApplicationPerson);
  }

  private existingPersonValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      return value && typeof value !== 'string' && value.id ? null : { personSelectionRequired: true };
    };
  }

  private searchExistingPeople(rawValue: string): void {
    const searchTerm = rawValue.trim();

    if (searchTerm.length < 3) {
      this.matchingPeople = [];
      this.searchCompleted = false;
      return;
    }

    const filter: IPersonFilter = {
      filterString: searchTerm,
      filterStatus: '',
      filterAffiliation: [],
      expirationStartDate: '',
      expirationEndDate: '',
      filterDiscipline: [],
    };
    const currentPersonId = this.person.person?.id ?? null;
    const existingPersonIds = new Set(
      (this.data.existingPeople || [])
        .map((currentPerson) => currentPerson.person?.id)
        .filter((personId): personId is number => !!personId && personId !== currentPersonId)
    );

    this.personService.getPeopleFiltered(filter).subscribe({
      next: (people) => {
        this.matchingPeople = (people || []).filter((person) => !existingPersonIds.has(person.id));
        this.searchCompleted = true;
      },
      error: () => {
        this.matchingPeople = [];
        this.searchCompleted = true;
        this.toastr.error('Unable to search for certified individuals.');
      }
    });
  }

  private getPersonFullName(person: IPerson): string {
    const parts = [person.firstName, person.middleName, person.lastName]
      .map((namePart) => (namePart || '').trim())
      .filter((namePart) => namePart.length > 0);

    return parts.join(' ');
  }

  private getPreferredEmail(person: IPerson): string | null {
    const directEmail = this.normalizeEmail((person as any)?.preferredEmail || (person as any)?.email);
    if (directEmail) {
      return directEmail;
    }

    const contactInformations = (person as any)?.contactInformations;
    if (!Array.isArray(contactInformations) || !contactInformations.length) {
      return null;
    }

    const emailCandidates = contactInformations
      .map((contactInformation) => this.toEmailCandidate(contactInformation))
      .filter((candidate): candidate is IEmailCandidate => candidate !== null)
      .sort((firstCandidate, secondCandidate) => {
        if (secondCandidate.typePriority !== firstCandidate.typePriority) {
          return secondCandidate.typePriority - firstCandidate.typePriority;
        }

        if (secondCandidate.recency !== firstCandidate.recency) {
          return secondCandidate.recency - firstCandidate.recency;
        }

        return firstCandidate.email.localeCompare(secondCandidate.email);
      });

    return emailCandidates.length ? emailCandidates[0].email : null;
  }

  private toEmailCandidate(contactInformationContainer: any): IEmailCandidate | null {
    const contactInformation = contactInformationContainer?.contactInformation || contactInformationContainer;
    const email = this.normalizeEmail(
      contactInformation?.email
      || contactInformation?.Email
      || contactInformationContainer?.email
      || contactInformationContainer?.Email
    );

    if (!email) {
      return null;
    }

    const contactType = (
      contactInformation?.contactType
      || contactInformation?.ContactType
      || contactInformationContainer?.contactType
      || contactInformationContainer?.ContactType
      || ''
    ).toString();

    const recency = Math.max(
      this.parseDateEpoch(contactInformation?.lastUpdated || contactInformation?.LastUpdated),
      this.parseDateEpoch(contactInformation?.createDate || contactInformation?.CreateDate),
      this.parseDateEpoch(contactInformationContainer?.lastUpdated || contactInformationContainer?.LastUpdated),
      this.parseDateEpoch(contactInformationContainer?.createDate || contactInformationContainer?.CreateDate),
    );

    return {
      email,
      typePriority: this.getEmailTypePriority(contactType),
      recency,
    };
  }

  private normalizeEmail(value: string | null | undefined): string | null {
    const normalizedValue = (value || '').trim();
    return normalizedValue.length ? normalizedValue : null;
  }

  private parseDateEpoch(value: unknown): number {
    if (!value) {
      return 0;
    }

    const parsedDate = new Date(value as string).getTime();
    return Number.isNaN(parsedDate) ? 0 : parsedDate;
  }

  private getEmailTypePriority(contactType: string): number {
    const normalizedType = (contactType || '').trim().toLowerCase();

    if (!normalizedType.length) {
      return 0;
    }

    if (normalizedType === 'work' || normalizedType === 'business' || normalizedType === 'primary' || normalizedType === 'main') {
      return 3;
    }

    if (normalizedType === 'home' || normalizedType === 'personal') {
      return 2;
    }

    return 1;
  }
}